/* eslint-disable no-console */
import { useAtom } from 'jotai';
import { useState, useEffect, useCallback } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import {
  processInitialDataEstadoOk,
  shouldHideFooter,
  shouldShowMinimalFooter,
} from './AuthenticatedNativo';
import ROUTES from './routes';

import { Footer, FooterComplex, Splash } from '@/components';
import useAuth from '@/hooks/useAuth';
import useCountMedals from '@/hooks/useCountMedals';
import usePinsAccount from '@/hooks/usePinsAccount';
import useToast from '@/hooks/useToast';
import { useTournament } from '@/hooks/useTournament';
import useTshirtAndShield from '@/hooks/useTshirtAndShield';
import {
  sendZondaAccessToken,
  zondaGetAccessToken,
  zondaGetUserProfile,
  zondaSyncSession,
} from '@/services/zonda';
import { recessPosterAtom } from '@/store';
import { TEXTS } from '@/utils';

/**
 * AuthenticatedZonda
 * <br/>
 * Login vía Identidad Zonda (reemplazo de Auth0). GDT es un subdominio de
 * clarin.com y hereda la sesión ya establecida por Clarín — no procesa un
 * callback propio (ver "Integración Gran DT", Confluence GDT-4377772033).
 * Por eso, a diferencia de AuthenticatedOTP, esto corre en cada carga
 * (como AuthenticatedNativo) sincronizando la sesión de Zonda en vez de
 * reaccionar a un parámetro de url.
 * <br/>
 * Gateado por el flag `enableZondaAuth` (ver useAuth y services/zonda).
 * @see {useAuth}
 */
export const AuthenticatedZonda = () => {
  const [isLoading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { show } = useToast();
  const { handleSetTournamentMaps } = useTournament();
  const { handleSetPinsData, pinsData } = usePinsAccount();
  const { handleSetMedalsData } = useCountMedals();

  const { isAuthenticated, logout, setUserInitialData, setStateLoginViaZonda } =
    useAuth();

  const { setTshirtAndShield } = useTshirtAndShield();
  const location = useLocation();

  const [recessPoster] = useAtom(recessPosterAtom);
  // Root.tsx only calls rememberZondaConfig() (a plain module-level write,
  // not reactive) inside an effect that runs *after* this component's own
  // mount effect — React fires passive effects children-first. Gating on
  // this atom (set in that same Root.tsx effect) instead of a mount-once
  // effect ensures we wait for it, same pattern Authenticated.tsx uses for
  // auth0Loading.
  const zondaConfigReady = Boolean(recessPoster?.zonda);

  const handleRedirectAfterLogin = useCallback(async () => {
    if (!zondaConfigReady) {
      return;
    }

    setUserInitialData(null);

    if (isAuthenticated) {
      setLoading(false);
      return;
    }

    try {
      setStateLoginViaZonda({
        isLoggedInViaZonda: false,
        isLoading: true,
        isUsingZonda: true,
      });

      await zondaSyncSession(); // necesario?
      const profile = zondaGetUserProfile();

      if (!profile) {
        // Sin sesión de Zonda todavía: no es un error, el usuario aún no inició sesión.
        setStateLoginViaZonda({
          isLoggedInViaZonda: false,
          isLoading: false,
          isUsingZonda: true,
        });
        setLoading(false);
        return;
      }

      const accessToken = zondaGetAccessToken();
      if (!accessToken) {
        throw new Error(
          'No se pudo obtener el access_token de Zonda (ver TODO en services/zonda.ts)'
        );
      }

      const result = await sendZondaAccessToken({ accessToken });

      if (result && result.estado) {
        setStateLoginViaZonda({
          isLoggedInViaZonda: true,
          isLoading: false,
          isUsingZonda: true,
        });
        await processInitialDataEstadoOk(
          result,
          setUserInitialData,
          setTshirtAndShield,
          handleSetTournamentMaps,
          handleSetPinsData,
          handleSetMedalsData,
          navigate,
          location
        );
        setLoading(false);
      } else {
        setStateLoginViaZonda({
          isLoggedInViaZonda: false,
          isLoading: false,
          isUsingZonda: false,
        });
        setUserInitialData(null);

        if (result) {
          switch (result.codError) {
            case 7:
            case 14:
            case 18:
            case 16:
            case undefined: {
              show(result.mensaje);
              console.log('1-zonda');
              logout();
              break;
            }

            case 10: {
              if (location.pathname === '/preguntas-frecuentes') {
                navigate(ROUTES.preguntasFrecuentes, {
                  replace: true,
                });
                setLoading(false);
              } else {
                setStateLoginViaZonda({
                  isLoggedInViaZonda: true,
                  isLoading: false,
                  isUsingZonda: true,
                });
                navigate(ROUTES.registration, {
                  replace: true,
                });
                setLoading(false);
              }
              break;
            }

            default: {
              console.log('2.1-zonda');
              console.log(result.codError);
              show(TEXTS.defaultError);
              logout();
            }
          }
        } else {
          console.log('3-zonda');
          show(TEXTS.defaultError);
          logout();
        }
      }
    } catch (error) {
      const err = error as Error;
      show(err.message);
      navigate(ROUTES.notFound, {
        replace: true,
      });
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zondaConfigReady]);

  useEffect(() => {
    handleRedirectAfterLogin();
  }, [handleRedirectAfterLogin]);

  if (isLoading) {
    return <Splash />;
  }

  // oculto el footer para urls determinadas
  const showMinimalFooter =
    shouldShowMinimalFooter(window.location.pathname) ||
    (!isAuthenticated &&
      window.location.pathname === ROUTES.preguntasFrecuentes);

  // oculto el footer para urls determinadas
  const hideFooter = shouldHideFooter(window.location.pathname);

  return (
    <>
      <Outlet />
      {!hideFooter &&
        (showMinimalFooter ? (
          <Footer />
        ) : (
          <FooterComplex isPremium={pinsData.isPremium} />
        ))}
    </>
  );
};

AuthenticatedZonda.displayName = 'AuthenticatedZonda';

export default AuthenticatedZonda;
